import {
  MouseEvent,
  ReactNode,
  isValidElement,
  useEffect,
  useRef,
  useState,
  DetailedHTMLProps,
  HTMLAttributes,
} from 'react';
import { detectTouch } from '../../util/util';
import { useOnClickOutside } from '../../util/use-on-click-outside';
import { Typography } from '../typography/typography';
import styles from './tooltip.module.scss';
import classNames from 'classnames';
import { Portal } from '../portal/portal';

export const Tooltip = ({
  tooltip,
  children,
  type = 'white',
  invisible = false,
  allowBreakpoints = false,
  maxWidth = '',
  ...props
}: React.PropsWithChildren<{
  tooltip: ReactNode | string;
  type?: 'white' | 'black';
  invisible?: boolean;
  allowBreakpoints?: boolean;
  maxWidth?: string;
}> &
  DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>) => {
  const isTouchDevice = detectTouch();
  const TOOLTIP_DELAY = 500;
  const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(false);
  const [, setTooltipDelayTimeout] = useState<NodeJS.Timeout>();
  const [hovered, setHovered] = useState<boolean>(false);
  const [tooltipHovered, setTooltipHovered] = useState<boolean>(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  function handleMouseMove(event: MouseEvent<HTMLDivElement>) {
    if (!tooltipHovered) {
      setCoords({
        x: event.pageX,
        y: event.pageY,
      });
    }
  }

  useEffect(() => {
    setTooltipDelayTimeout((current) => {
      clearTimeout(current);
      return setTimeout(() => setIsTooltipVisible(tooltipHovered || hovered), TOOLTIP_DELAY);
    });
  }, [tooltipHovered, hovered]);

  function calcTop(): number {
    let top = (containerRef.current?.getBoundingClientRect().top || 0) - (tooltipRef.current?.clientHeight || 0);
    if (top < 0) {
      top = containerRef.current?.getBoundingClientRect().bottom || 0;
    }
    return top + window.scrollY;
  }

  useOnClickOutside(containerRef, () => setIsTooltipVisible(false));

  const handleScroll = () => {
    isTouchDevice && setIsTooltipVisible(false);
  };

  useEffect(() => {
    const isContainedInIframe = window.location !== window.parent.location;

    if (isTooltipVisible && isContainedInIframe) {
      const legacyView = document.getElementById('body');
      legacyView?.dispatchEvent(new Event('components-loaded'));
    }
  }, [isTooltipVisible]);

  useEffect(() => {
    if (isTouchDevice) {
      document.addEventListener('scroll', handleScroll, true);
      return () => {
        document.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, []);

  const tooltipContent = (
    <div
      ref={tooltipRef}
      onMouseLeave={() => setTooltipHovered(false)}
      onMouseEnter={() => setTooltipHovered(true)}
      className={classNames(styles.tooltip, styles[type])}
      style={{
        position: 'fixed',
        left: Math.max(coords.x - (tooltipRef.current?.clientWidth || 0) / 2, 0),
        top: calcTop(),
        visibility: isTooltipVisible && !invisible && tooltipRef.current?.clientHeight ? 'visible' : 'hidden',
        maxWidth,
      }}
    >
      <div className="tooltip-content">
        {isValidElement(tooltip) ? (
          tooltip
        ) : (
          <div className="p-[12px]">
            <Typography
              type="text-sm"
              className={classNames({
                '!text-quipli-white-50': type === 'black',
                '!text-quipli-black-800': type === 'white',
                'whitespace-pre-line': allowBreakpoints,
              })}
            >
              {tooltip}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onClick={() => isTouchDevice && setIsTooltipVisible(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseEnter={() => setHovered(true)}
      {...props}
    >
      {children}
      {(tooltipHovered || hovered || isTooltipVisible) && <Portal zIndex={9999}>{tooltipContent}</Portal>}
    </div>
  );
};
